/*
 * Controlled drugs on the tablet: what the register says is in the cupboard, booking stock in,
 * the witnessed count, and sending stock back. Two people sign every line.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Witness } from './api.ts';
import { Overlay } from './overlay.tsx';

type CdMedicine = {
  id: string;
  name: string;
  doseText: string;
  residentId: string;
  residentName: string;
  balance: number;
  lastCheck: string | null;
};

type CdEntry = {
  id: string;
  kind: 'received' | 'given' | 'returned' | 'check';
  qty: number;
  balanceAfter: number;
  counted: number | null;
  difference: number | null;
  supplier: string;
  reference: string;
  note: string;
  recordedAt: string;
  recordedBy: string;
  witness: string;
};

type Job = 'receive' | 'check' | 'return';

const JOB_TITLE: Record<Job, string> = {
  receive: 'Book stock in',
  check: 'Count what is there',
  return: 'Send stock back',
};

export function CdScreen({ homeId }: { homeId: string }) {
  const [medicines, setMedicines] = useState<CdMedicine[] | null>(null);
  const [witnesses, setWitnesses] = useState<Witness[]>([]);
  const [doing, setDoing] = useState<{ job: Job; med: CdMedicine } | null>(null);
  const [showing, setShowing] = useState<CdMedicine | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ medicines: CdMedicine[] }>(`/api/homes/${homeId}/cd`);
      setMedicines(data.medicines);
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the register.');
    }
  }, [homeId]);

  useEffect(() => {
    void load();
    api
      .get<{ witnesses: Witness[] }>(`/api/homes/${homeId}/witnesses`)
      .then((data) => setWitnesses(data.witnesses))
      .catch(() => setWitnesses([]));
  }, [homeId, load]);

  return (
    <section>
      <div className="head">
        <h1>Controlled drugs</h1>
        <button type="button" className="quiet" onClick={() => void load()}>Refresh</button>
      </div>
      <p className="muted">
        The running balance comes from the register itself. Doses given on the round are taken off
        automatically, so the two can never disagree.
      </p>

      {error ? <p className="error" role="alert">{error}</p> : null}
      {medicines === null ? <p className="muted">Loading…</p> : null}
      {medicines && !medicines.length ? <p className="muted">No controlled drugs at this home.</p> : null}

      <ul className="list">
        {(medicines ?? []).map((med) => {
          const overdue = checkOverdue(med.lastCheck);
          return (
            <li key={med.id}>
              <div className="row static column dose">
                <div className="row-line">
                  <span className="row-main">{med.name}</span>
                  <span className="time">{med.balance}</span>
                </div>
                <p className="muted small">{med.residentName} · {med.doseText}</p>

                <div className="chips">
                  <span className={`chip ${overdue ? 'warn' : ''}`}>
                    {med.lastCheck ? `Counted ${formatDate(med.lastCheck)}` : 'Never counted'}
                  </span>
                </div>

                <div className="row-actions">
                  <button type="button" onClick={() => setDoing({ job: 'check', med })}>Count it</button>
                  <button type="button" className="quiet" onClick={() => setDoing({ job: 'receive', med })}>Book in</button>
                  <button type="button" className="quiet" onClick={() => setDoing({ job: 'return', med })}>Send back</button>
                  <button type="button" className="quiet" onClick={() => setShowing(med)}>The register</button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {doing ? (
        <CdDialog
          job={doing.job}
          med={doing.med}
          witnesses={witnesses}
          onClose={() => setDoing(null)}
          onDone={() => {
            setDoing(null);
            void load();
          }}
        />
      ) : null}

      {showing ? <RegisterDialog med={showing} onClose={() => setShowing(null)} /> : null}
    </section>
  );
}

function CdDialog({
  job,
  med,
  witnesses,
  onClose,
  onDone,
}: {
  job: Job;
  med: CdMedicine;
  witnesses: Witness[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(job === 'check' ? String(med.balance) : '');
  const [witnessId, setWitnessId] = useState('');
  const [supplier, setSupplier] = useState('AYP Healthcare');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ balance: number; difference?: number | null } | null>(null);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        const body: Record<string, unknown> = { medicineId: med.id, witnessId, note };
        if (job === 'check') body.counted = Number(amount);
        else body.qty = Number(amount);
        if (job === 'receive') {
          body.supplier = supplier;
          body.reference = reference;
        }
        const data = await api.post<{ balance: number; difference?: number | null }>(`/api/cd/${job}`, body);
        setResult(data);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That did not save.');
      } finally {
        setBusy(false);
      }
    },
    [job, med.id, amount, witnessId, note, supplier, reference],
  );

  if (result) {
    const mismatch = typeof result.difference === 'number' && result.difference !== 0;
    return (
      <Overlay onClose={onDone}>
        <div className="card wide">
          <h2>{mismatch ? 'The count does not match' : 'Recorded'}</h2>
          <p className="password">{result.balance}</p>
          <p className="muted">The register now shows {result.balance} for {med.name}.</p>
          {mismatch ? (
            <p className="notice warn">
              That is {Math.abs(result.difference ?? 0)} {(result.difference ?? 0) < 0 ? 'fewer' : 'more'} than the
              register expected. It has been written down as it stands. Tell the manager and the pharmacy today.
            </p>
          ) : null}
          <button type="button" onClick={onDone}>Done</button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <form className="card wide" onSubmit={submit}>
        <h2>{JOB_TITLE[job]}</h2>
        <p className="muted">{med.name} · {med.residentName}</p>
        <p className="muted small">The register currently shows {med.balance}.</p>

        <label htmlFor="cd-amount">{job === 'check' ? 'How many did you count?' : 'How many?'}</label>
        <input id="cd-amount" type="number" inputMode="decimal" step="0.5" min="0" required
          value={amount} onChange={(e) => setAmount(e.target.value)} />

        {job === 'receive' ? (
          <>
            <label htmlFor="cd-supplier">Where from</label>
            <input id="cd-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
            <label htmlFor="cd-ref">Delivery note or prescription number</label>
            <input id="cd-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </>
        ) : null}

        <label htmlFor="cd-witness">Second person</label>
        <select id="cd-witness" required value={witnessId} onChange={(e) => setWitnessId(e.target.value)}>
          <option value="">Choose who did this with you</option>
          {witnesses.map((w) => (
            <option key={w.id} value={w.id}>{w.name} — {w.jobTitle}</option>
          ))}
        </select>

        <label htmlFor="cd-note">Note (optional)</label>
        <input id="cd-note" value={note} onChange={(e) => setNote(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save this line'}</button>
          <button type="button" className="quiet" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </Overlay>
  );
}

function RegisterDialog({ med, onClose }: { med: CdMedicine; onClose: () => void }) {
  const [entries, setEntries] = useState<CdEntry[] | null>(null);

  useEffect(() => {
    api
      .get<{ entries: CdEntry[] }>(`/api/medicines/${med.id}/cd`)
      .then((data) => setEntries(data.entries))
      .catch(() => setEntries([]));
  }, [med.id]);

  return (
    <Overlay onClose={onClose}>
      <div className="card wide">
        <h2>{med.name}</h2>
        <p className="muted">{med.residentName}</p>

        {entries === null ? <p className="muted">Loading…</p> : null}
        {entries && !entries.length ? <p className="muted">Nothing in the register yet.</p> : null}

        {entries && entries.length ? (
          <div className="scroll">
            <table className="register">
              <thead>
                <tr>
                  <th>When</th>
                  <th>What</th>
                  <th className="num">In</th>
                  <th className="num">Out</th>
                  <th className="num">Balance</th>
                  <th>Signed</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className={e.difference ? 'mismatch' : ''}>
                    <td>{formatWhen(e.recordedAt)}</td>
                    <td>
                      {label(e)}
                      {e.note ? <small>{e.note}</small> : null}
                      {e.reference ? <small>{e.reference}</small> : null}
                    </td>
                    <td className="num">{e.kind === 'received' ? e.qty : ''}</td>
                    <td className="num">{e.kind === 'given' || e.kind === 'returned' ? e.qty : ''}</td>
                    <td className="num">{e.balanceAfter}</td>
                    <td>
                      {e.recordedBy}
                      {e.witness ? <small>with {e.witness}</small> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <button type="button" onClick={onClose}>Close</button>
      </div>
    </Overlay>
  );
}

function label(entry: CdEntry): string {
  if (entry.kind === 'received') return `Booked in${entry.supplier ? ` from ${entry.supplier}` : ''}`;
  if (entry.kind === 'given') return 'Given';
  if (entry.kind === 'returned') return 'Sent back';
  if (entry.difference) {
    return `Counted ${entry.counted}, register said ${Number(entry.counted) - Number(entry.difference)}`;
  }
  return `Counted ${entry.counted}, correct`;
}

function checkOverdue(lastCheck: string | null): boolean {
  if (!lastCheck) return true;
  return Date.now() - new Date(lastCheck).getTime() > 7 * 86_400_000;
}

function formatDate(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)}`;
}

function formatWhen(iso: string): string {
  const when = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(when.getDate())}/${pad(when.getMonth() + 1)} ${pad(when.getHours())}:${pad(when.getMinutes())}`;
}
